'use client';

import { useEffect, useState } from 'react';

import { css } from 'styled-system/css';

type Props = {
  /** id of the server-rendered scroll-snap list the dots follow. */
  listId: string;
  count: number;
};

const dotsClass = css({ display: { base: 'flex', xs: 'none' }, alignItems: 'center', gap: '6px' });
const dotClass = css({
  w: '6px',
  h: '6px',
  borderRadius: 'full',
  bg: 'rgba(255, 255, 255, 0.3)',
  transition: 'background-color .2s, width .25s',
  // One class per property: the active state is a selector, not a second atomic class.
  '&[data-active]': { w: '18px', borderRadius: '3px', bg: 'white' },
});

// Mobile-only position indicator for the hero's Explore carousel. Decorative (aria-hidden): the
// cards themselves are links in the SSR HTML. Layout is read only inside scroll events, never on mount.
export const HeroExploreDots = ({ listId, count }: Props) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const list = document.getElementById(listId);
    if (!list) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const items = Array.from(list.children) as HTMLElement[];
      if (list.scrollLeft + list.clientWidth >= list.scrollWidth - 4) {
        setActive(items.length - 1);
        return;
      }
      const origin = items[0]?.offsetLeft ?? 0;
      let best = 0;
      let bestDistance = Infinity;
      items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - origin - list.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      setActive(best);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      list.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [listId]);

  return (
    <div className={dotsClass} aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={dotClass} data-active={index === active ? '' : undefined} />
      ))}
    </div>
  );
};
